import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useApi } from '@/lib/ConfigProvider'
import { useSession } from '@/auth/SessionProvider'
import { fetchOnboarding } from './onboarding'
import type { OnboardingState } from './onboarding'

interface OnboardingContextValue {
  readonly state: OnboardingState
  readonly refresh: () => Promise<void>
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const api = useApi()
  const { state: session } = useSession()
  const [state, setState] = useState<OnboardingState>({ status: 'loading' })

  // WHICH ANSWER IS STILL THE CURRENT ONE (#57).
  //
  // Two refreshes can be in flight at once — the "Check again" button is one
  // click away from the failure state, and clicking it twice is the ordinary
  // human response to a screen that is not answering. Without this, they land in
  // whatever order the network gives them, so a slow FAILURE can arrive after a
  // fast success and overwrite it: the customer is shown "we cannot tell you
  // where your checks stand" about a read that had already succeeded.
  //
  // That is the onboarding rule again in a new disguise. "We could not find out"
  // displayed after we did find out is the same false statement #22 and #54 were
  // about, reached by a different route.
  //
  // A counter rather than an AbortController: cancelling is a larger change with
  // its own seam, and is not needed to stop the WRONG ANSWER being displayed.
  // Ignoring a superseded result degrades safely — the worst case is a dropped
  // update, never an incorrect one.
  const generation = useRef(0)

  const refresh = useCallback(async () => {
    const mine = ++generation.current
    // Onboarding is per-customer, so there is nothing to ask about until we
    // know who is asking.
    if (session.status !== 'authenticated') {
      setState({ status: 'loading' })
      return
    }
    const next = await fetchOnboarding(api)
    if (mine !== generation.current) return
    setState(next)
  }, [api, session.status])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<OnboardingContextValue>(() => ({ state, refresh }), [state, refresh])

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext)
  if (ctx === null) throw new Error('useOnboarding must be used inside an OnboardingProvider')
  return ctx
}
